import React from 'react'
import { BracketType, useGetPhaseBracketQuery } from '@plvs/graphql/generated'
import { makeStyles } from '@material-ui/core'
import { Box, WaitTillLoaded } from '@plvs/respawn/features/layout'
import StandingSecondaryLargeDark from '@plvs/respawn/assets/icons/StandingSecondaryLargeDark.svg'
import StandingSecondaryLarge from '@plvs/respawn/assets/icons/StandingSecondaryLarge.svg'
import { useEsport as useNexusEsport } from '@playvs-inc/nexus-theme'
import { EmptyPageWithSpot } from '@plvs/respawn/components/empty/EmptyPageWithSpot'
import { NxTab, NxTabs } from '@playvs-inc/nexus-components'
import { useProcessedRounds } from '@plvs/respawn/containers/bracket/hooks/useProcessedRounds'
import { BracketPhase } from '@plvs/respawn/containers/bracket/hooks/useProcessedRounds.utils'
import { getBracketComponent } from '@plvs/respawn/containers/bracket/BracketContainer.helpers'

const useStyles = makeStyles((theme) => ({
  container: {
    overflowX: 'auto',
    maxWidth: '1267px',
    '&::-webkit-scrollbar': {
      // overrides default scrollbar height of 0 in ssr.tsx
      height: '0.5em !important',
      background: theme.palette.ColorBackgroundAlt,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.ColorBackgroundInactive,
    },
  },
}))

export interface BracketContainerProps {
  phaseId: string
  esportSlug: string
}

export function BracketContainer({
  phaseId,
  esportSlug,
}: BracketContainerProps): React.ReactElement {
  const classes = useStyles()

  const { data: bracketData, loading } = useGetPhaseBracketQuery({
    variables: { phaseId },
  })
  const esport = useNexusEsport(esportSlug)
  const overlayColor = esport?.palette.baseColor || ''
  const isDoubleElimination =
    bracketData?.getBracketByPhaseId?.type === BracketType.DoubleElimination

  const [bracketPhase, setBracketPhase] = React.useState<BracketPhase | null>(
    BracketPhase.Upper
  )

  const {
    processedRounds,
    processedUpperRounds,
    processedLowerRounds,
    processedFinalRounds,
  } = useProcessedRounds({ bracketData, esportSlug })
  const hasRounds = processedRounds.length > 0

  return (
    <WaitTillLoaded loading={loading} showSpinnerWhileLoading>
      {isDoubleElimination && (
        <Box p={3}>
          <NxTabs
            onChange={(_event, value): void => setBracketPhase(value)}
            value={bracketPhase}
          >
            <NxTab label="Upper Bracket" value={BracketPhase.Upper} />
            <NxTab label="Lower Bracket" value={BracketPhase.Lower} />
            <NxTab label="Finals" value={BracketPhase.Finals} />
          </NxTabs>
        </Box>
      )}

      {hasRounds ? (
        <Box className={classes.container} data-testid="BracketContainer">
          {getBracketComponent({
            overlayColor,
            isDoubleElimination,
            bracketPhase,
            rounds: processedRounds,
            upperRounds: processedUpperRounds,
            lowerRounds: processedLowerRounds,
            finalRounds: processedFinalRounds,
          })}
        </Box>
      ) : (
        <EmptyPageWithSpot
          spot={StandingSecondaryLarge}
          spotCaption="No standings"
          spotDark={StandingSecondaryLargeDark}
          subtitle="An updated bracket will be available after the first round is played."
          title="Bracket not available"
        />
      )}
    </WaitTillLoaded>
  )
}
